/**
 * Reader — the page. Everything else in this directory serves this file.
 *
 * design.md §5. The product surface, and the riskiest bet in the design (§13.1): CSS multi-column
 * pagination inside a transform-translated track, on mobile Safari.
 *
 * ── The one idea ────────────────────────────────────────────────────────────
 * **The sentence id is the only coordinate system.** Nothing here is addressed by seconds, pixels,
 * pages, bytes or file boundaries. The reader's place is a `sid`; the pin across a relayout is a
 * `sid`; the deep link is a `sid`; the saved position is a `sid` and its `editionId`. The page
 * number in the footer is display state that is computed after the fact and never stored (§5.1:
 * "Page numbers are never persisted").
 *
 * That is what makes the P1 gate fall out for free rather than needing code:
 *   · change the font size mid-page and land on the same sentence
 *   · rotate to landscape and land on the same sentence
 *   · close the tab, reopen on a MacBook, resume where you were
 * There is no "restore scroll position" anywhere in this file, because there is nothing to restore.
 *
 * ── P2 plugged into P1's seams, and did not widen them ──────────────────────
 * `useCueSync` runs *here* and not above this component, for one reason: the read-along's turn is
 * driven by `columnOf(sid)`, and the column map is a property of a layout only the `Paginator`
 * inside this file has measured. Everything that is not layout — which element is playing, which
 * artifact is loaded, whether the GPU has produced this page yet — arrives through
 * `ReaderProps.narration` and is owned by whoever mounts this component.
 *
 * The three P1 seams were used as written and none of them moved:
 *   · `ReaderProps.narration` — `repaginate()` already preferred `spokenSid` over the top-visible
 *     sentence, which is §5.3's `ctx.spokenSid ?? ctx.topVisibleSid()`, and already suppressed it
 *     while `following` is false.
 *   · `usePageTurn`'s `following` flag and `narrationColumn` — LAW 12, enforced since P1.
 *   · the follow pill's place in the layout — now `FollowPill`, which says which page.
 *
 * ⚠️ **Wired.** `Read.tsx` mounts this with `narration={useNarration(book)}`, and
 * `client/src/audio/AudioEngine.ts` satisfies `ReaderNarration.clock` structurally —
 * `test/cue-sync.test.ts` asserts that assignment, so a rename on either side is a type error rather
 * than a silent fallback. Nothing in this file constructs an engine, mints a media key, or calls
 * Media Session; every one of those reaches it as a value or a callback on the plug.
 *
 * The one thing this file does own that looks like transport and is not: `followToSid`. The audio
 * walks the whole book and this component mounts one chapter, so somebody has to move the screen to
 * where the voice is. LAW 12 makes that legal in exactly one direction and it is gated on
 * `following`; see the comment on `followToSid` below.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";

import type { Block, BookVisibility, Chapter } from "../../../shared/doc.ts";
import { continuesSentence } from "../../../shared/text/flow.ts";
import { blockIdFrom, chapterOrdFrom, compareOrdered } from "../../../shared/address.ts";
import type { AccessBook, ShareGrantKind } from "../../../shared/access.ts";
import { canRegenerate } from "../../../shared/access.ts";
import type { StoredPosition } from "../../../shared/position.ts";

import { useAuth } from "../lib/auth.tsx";
import { randomId } from "../lib/id.ts";

import { Paginator, geometryFor, type Geometry } from "./Paginator.ts";
import {
  imagesSettled,
  onViewportChange,
  repaginate,
  type Landing,
  type RepaginateCause,
} from "./repaginate.ts";
import { renderBlock } from "./renderBlock.ts";
import { usePageTurn, type TurnSource } from "./usePageTurn.ts";
import {
  seekTargetInPage,
  useCueSync,
  type CueSyncEvent,
  type NarrationClock,
} from "./useCueSync.ts";
import { FollowPill } from "./FollowPill.tsx";
import { ListenView, type ListenControls } from "./ListenView.tsx";
import { PlaybackBar } from "./PlaybackBar.tsx";
import { WaitingBanner } from "./WaitingBanner.tsx";
import { Scrubber } from "./Scrubber.tsx";
import { validatePageAudio, type PageState } from "../../../shared/narration/cue.ts";
import {
  READER_FONTS,
  READER_SCALES,
  READER_THEMES,
  measureBucket,
  readFontMetrics,
  useTypography,
} from "./useTypography.ts";
import { ChapterDrawer } from "./ChapterDrawer.tsx";
import { SearchPanel } from "./SearchPanel.tsx";
import { ProgressFooter } from "./ProgressFooter.tsx";
import { SentenceMenu, useSentenceMenu } from "./SentenceMenu.tsx";
import { PersistentReactionMarks } from "./PersistentReactions.tsx";
import { SelectionChip, useSelectionMode } from "./SelectionMode.tsx";
import {
  PositionWriter,
  buildOrdIndex,
  chooseOpening,
  fetchPositions,
  loadLocal,
  makePosition,
  newestPlace,
  validSid,
  type OrdIndex,
} from "../lib/position.ts";

import "../ui/tokens.css";
import "../ui/fonts.css";
import "../ui/reader.css";
import "./reader-page.css";

/* ══════════════════════════════════════════════════════════════════════════
 * The shapes this reader consumes, from the §10 API surface.
 *
 * Exported so the page that mounts `<Reader>` and the route that serves it can agree in the type
 * system rather than in a comment. Tolerance is about the *envelope* only — whether the route
 * answers `{ book: … }` or the bare object — never about contents: a chapter with no `blocks` is
 * an error the reader shows, not a default it invents (LAW 9, absent ≠ zero).
 * ══════════════════════════════════════════════════════════════════════════ */

export interface ReaderBook {
  id: string;
  title: string;
  author?: string | null;
  /** LAW 1's other half. Every sid this reader touches is checked against it. */
  currentEditionId: string | null;
  narrationStyle?: string | null;
  lang?: string | null;
  /**
   * The four fields `shared/access.ts` actually reads, carried verbatim off the wire.
   *
   * **LAW 13.** The sentence menu computes its rows by calling `canRegenerate`/`canEdit` with these,
   * never from a tier string or a build flag — and the server answers the same question again,
   * authoritatively, with the same function. They are carried rather than collapsed into a
   * `canEdit: true` for the reason `routes/books.ts` states at the same seam: a mapping step is
   * where a `takenDownAt` gets dropped, and a taken-down book then grows an edit menu.
   */
  ownerId?: string | null;
  visibility?: BookVisibility | null;
  takenDownAt?: number | null;
  deletedAt?: number | null;
  /**
   * P4 (§6.4). What a live share link this browser holds confers on this book — the fifth field
   * `shared/access.ts` reads, carried verbatim off the wire for the same reason as the four above.
   *
   * ⚠️ Dropping it here has no visible symptom: the reader works, the audio plays, and the one thing
   * that silently stops existing is `allowRegenerate` — §0 decision 1's *only* escape hatch, the
   * per-link door the owner deliberately opened. It would be greyed out in the menu while the server
   * cheerfully accepted the request, which is LAW 13's divergence in the direction nobody reports.
   */
  grants?: readonly ShareGrantKind[] | null;
}

export interface ChapterPayload {
  chapter: Chapter;
  blocks: Block[];
  /**
   * `sentences.globalOrd` of this chapter's first sentence, if the route supplies it. One integer
   * that upgrades every ordinal in the chapter from derived to exact — see lib/position.ts.
   */
  startOrd?: number | null;
}

/**
 * The narration plug. Passing nothing keeps the reader exactly as P1 shipped it.
 *
 * ── What this component does and does not own ───────────────────────────────
 * **Owns:** the wash, when the page turns, which column the voice is on, whether the read-along is
 * trustworthy, and the two controls (`PlaybackBar`, `Scrubber`) that sit under the frame.
 * **Does not own:** the `<audio>` elements, the unlock gesture, the ping-pong between artifacts,
 * Media Session, the media key, or which page is loaded. Those are §5.7's `client/src/audio/`, and
 * every one of them reaches this file as a value or a callback below.
 *
 * The split is not bureaucratic. §5.7's engine keeps *two* elements alive and swaps them so a page
 * boundary has no audible gap; a reader that could reach in and set `currentTime` would eventually
 * set it on the wrong one, and the symptom is a book that plays the same page twice with nothing in
 * the console. So the reader asks — `onSeek`, `onPlayPause` — and reads a clock it cannot write.
 */
export interface ReaderNarration {
  /**
   * The sentence being spoken, when something *other than this reader* computes it: P1's seam, and
   * P5's co-read follower. Ignored while `page` is driving `useCueSync`, which is the P2 path.
   */
  spokenSid?: string | null;
  /** Likewise for the column. */
  column?: number | null;

  /* ── P2 ──────────────────────────────────────────────────────────────── */

  /**
   * The current page's `PageAudio`, **straight off the wire and not pre-validated**.
   *
   * Deliberately `unknown`. LAW 9 — absent ≠ zero — is checked in `useCueSync.setPage`, because
   * that is the only place that can both refuse the page and put the refusal on screen. A caller
   * that pre-validated and passed `PageAudio` would have had to decide what to do with a failure,
   * and the tempting decision is `?? 0`.
   *
   * Identity matters: pass a stable object. A new object each render re-arms every guard.
   */
  page?: unknown;
  /**
   * Whichever `<audio>` element is live, plus `playing` and `playbackRate`. Read, never written.
   * `AudioEngine` satisfies this structurally — no adapter, and a rename on either side is a type
   * error rather than a silent fallback.
   *
   * ⚠️ There is **no separate `rate` prop.** The speed the button shows and the speed the cue lead is
   * converted through are the same number, and giving the reader its own copy is how the label says
   * 1.5× while the highlight is computed for 1×. `PlaybackBar` reads it from here.
   */
  clock?: NarrationClock | null;
  /** Must be React state upstream — the rAF loop stops itself and cannot observe a bare mutation. */
  playing?: boolean;
  /**
   * Where animation frames come from. Pass the engine's tick subscription; omitting it opens a
   * second rAF loop beside the engine's, which is two wakeups per frame on a locked phone.
   */
  frames?: ((tick: () => void) => () => void) | null;

  /** The planner's answer for this page. Drives the "warming up — about 20 seconds" stall copy. */
  state?: PageState;
  /** Characters this page still owes the GPU, so the stall estimate is this page's and not a guess. */
  pendingChars?: number | null;
  /**
   * **Which page `state` is about.** Not necessarily the page on the deck: while the engine is
   * `waiting`, the deck's page is finished and `ready` and the wait is about the next one.
   *
   * The reader itself does not read this — it is here because `Room.tsx` composes a `ReaderNarration`
   * out of `useNarration`'s return and passes it down, so a field the room needs has to survive the
   * trip. §7.5's `{t:"notready", pageId}` is unreportable without it, and `ws.ts` drops a report with
   * no page id silently.
   */
  waitingPageId?: string | null;
  /** The planner's estimate for that page. LAW 9: `null` means not measured, never zero. */
  etaSec?: number | null;
  /** When the wait began — the clock of the WAIT, not of the poll. */
  waitStartedAt?: number | null;
  /**
   * **The engine has run out of page.** Not the same question as `state`, and the difference is the
   * whole of §7.5's honesty when the voice service is down: `ensure` fails fast rather than
   * enqueueing, so the plan says `missing`, and a block report keyed on the plan's string is never
   * sent. The reader itself does not read this — like `waitingPageId`, it is here because `Room.tsx`
   * composes a `ReaderNarration` out of `useNarration`'s return.
   */
  waiting?: boolean;
  /** The engine is loading and not playing — §7.5's `buffering`, for the same trip. */
  /**
   * The planner's reason a page is not ready, surfaced so `WaitingBanner` can name it instead of
   * showing a spinner. `useNarration` reads it from `plan.blocked`.
   */
  blocked?: { code?: string | null; message?: string | null } | null;
  stalled?: boolean;
  /** The estimate has elapsed and the job is still queued. One GPU; a queue is normal. */
  overdue?: boolean;

  /**
   * §5.7's chapter axis, which the scrubber and the lock screen must share or they disagree about
   * what "the track" is. `pageOffsetMs` is how much of this chapter precedes the *current page's*
   * artifact — the design's `chapterOffsetSec`, in milliseconds.
   */
  chapter?: { pageOffsetMs: number; durationMs: number } | null;

  onPlayPause?: (() => void) | null;
  onRate?: ((rate: number) => void) | null;
  /**
   * The explicit seek, and the only one. **LAW 3**: the target is a sentence and a fraction of it,
   * resolved against *this* client's cue list, never a millisecond taken on trust.
   */
  onSeek?: ((to: { sid: string; frac: number }) => void) | null;
  /**
   * A scrub that lands outside the loaded page. It is a page change, so the engine decides — this
   * reader has no book timeline and must not pretend to.
   */
  onSeekChapterMs?: ((ms: number) => void) | null;
  /** `POST /pages/:pageId/narration/ensure`. Omit for a reader who may not spend GPU (§4.8). */
  onNarrate?: (() => void) | null;
  /** §4.11's Retry on a terminal failure. Omit for a reader who may not spend GPU. */
  onRetry?: (() => void) | null;
  /** Whose voice. The pill says "Karti is on page 14", and a book read by `default` should not. */
  voice?: string;
  /** Desync, dropped highlights and §4.12's free `replay-sentence` signal. */
  onEvent?: ((ev: CueSyncEvent) => void) | null;
  /**
   * **Where a press of play should start**: the sentence the reader is currently looking at.
   *
   * The reader owns this and the narration plug cannot compute it — a saved position is stale the
   * moment somebody turns a page, and the audio engine deliberately has no idea which sentences are
   * on screen. Called on every landed relayout and page turn, which is a handful of times a minute,
   * never per frame.
   *
   * ⚠️ It must NOT be state upstream. It is written into a ref and read only inside a click handler;
   * making it a `useState` would re-render the reader on every page turn to store a value nothing
   * renders — which is the same cost `NarrationChrome` exists to avoid, arriving through the plug.
   */
  onAnchor?: ((sid: string | null) => void) | null;

  /* ── P3 ──────────────────────────────────────────────────────────────── */

  /**
   * §5.6's "Play from here", and LAW 12's only escape hatch: turning a page is browsing and never
   * moves the narration, so THIS is how a reader who has walked ahead says "read from what I am
   * looking at". Distinct from `onSeek`, which is an in-page fraction — this one may cross pages,
   * which only the narration plug can do.
   */
  onPlayFrom?: ((sid: string) => void) | null;
  /**
   * §4.11. Something on the server changed what one of these sentences says — a regeneration, a take
   * put back, a muted sentence, an applied pronunciation. The plug decides what to do about it; the
   * only rule the reader cares about is that **the restore is by `sid`**, never by page or by time.
   */
  onChanged?: ((r: { sids: string[]; jobs: { id: string; pageId: string; status: string }[] }) => void) | null;

  /* ── P5 ──────────────────────────────────────────────────────────────── */

  /**
   * **LAW 12's flag, on its way out of the reader.** §7.4: *"if you turn a page yourself,
   * `following = false` and a pill says so."*
   *
   * ⚠️ **The reader has always computed the real flag and never let anybody read it.**
   * `usePageTurn().following` is cleared by every manual turn and by `stopFollowing()` from the
   * chapter drawer, and it stayed entirely internal. `Room.tsx` therefore kept a *parallel*
   * `following` that starts `true` and is only ever moved by the room bar's Follow toggle — and that
   * is the one sent on `{t:"at"}`.
   *
   * The failure is in `read` mode with `waitForEveryone` on, which is the default. One member flips
   * back two pages to check a name: the reader clears its own flag, the room is told `following:
   * true`, and `Room.behindBlocks()` — which skips only `!m.following` members — counts them as
   * `behind`. Every `{kind:"advance"}` is then refused with *"Waiting for X"*, a block that carries
   * **no deadline** (LAW 20 exempts `behind` because it is derived and clears when the condition
   * does). The condition is "browsed away", the escape hatch is a flag the reader already set, and
   * the room could not see it. The only way out was noticing the Follow toggle.
   *
   * Fired on change only, from the same line that writes `followingRef`.
   */
  onFollowingChange?: ((following: boolean) => void) | null;
  /** A room-scoped, local-only navigation request. `seq` makes resyncing to the same sid repeatable. */
  followRequest?: { sid: string; seq: number } | null;
  /** When supplied by a room, keeps the reader's internal LAW 12 flag aligned with its chrome. */
  following?: boolean;
  /** Last sentence on the displayed page/spread, used for exact read-room containment. */
  onVisibleEnd?: ((sid: string | null) => void) | null;
  /** A deliberate forward page turn in read mode moves the room's reading anchor, not narration. */
  onReadAdvance?: ((sid: string) => void) | null;
  /** Which voice the takes and regenerations are keyed to. `null` means the book's default. */
  voiceId?: string | null;

  /* ── listening with the screen out of reach ──────────────────────────── */

  /**
   * ⟲15 / 30⟳, the sleep timer, and the full-screen Listen view. `null` in a room, whose controls
   * must move every member or none — these would move one.
   */
  listen?: ListenControls | null;
  /** Set by `Room.tsx`. The waiting banner's long-wait copy talks about the room only in a room. */
  inRoom?: boolean;
}

export interface ReaderProps {
  bookId: string;
  /** Open at this sentence, overriding the saved position and the `#s=` hash. */
  startSid?: string | null;
  /** Rendered as a back control in the bar when supplied. */
  onExit?: () => void;
  /** P2. */
  narration?: ReaderNarration | null;
  /** Social context. The persistent record remains book-scoped; this supplies tonight's names. */
  reactionRoomCode?: string | null;
  /** Echo a successfully stored reaction into the room's existing three-second floating transport. */
  onRoomReaction?: ((emoji: string, sid: string) => void) | null;
}

/* ══════════════════════════════════════════════════════════════════════════
 * Fetching
 * ══════════════════════════════════════════════════════════════════════════ */

class HttpError extends Error {
  readonly status: number;
  constructor(status: number, url: string) {
    super(`${status} on ${url}`);
    this.status = status;
  }
}

async function getJson(url: string, signal?: AbortSignal): Promise<unknown> {
  const res = await fetch(url, {
    credentials: "same-origin",
    headers: { accept: "application/json" },
    signal,
  });
  if (!res.ok) throw new HttpError(res.status, url);
  return res.json();
}

function unwrap(body: unknown, key: string): unknown {
  if (body && typeof body === "object" && !Array.isArray(body) && key in (body as object)) {
    return (body as Record<string, unknown>)[key];
  }
  return body;
}

function asBook(body: unknown): ReaderBook {
  const b = unwrap(body, "book") as Partial<ReaderBook> | null;
  if (!b || typeof b.id !== "string" || typeof b.title !== "string") {
    throw new Error("the book record is missing its id or title");
  }
  return {
    id: b.id,
    title: b.title,
    author: b.author ?? null,
    currentEditionId: typeof b.currentEditionId === "string" ? b.currentEditionId : null,
    narrationStyle: b.narrationStyle ?? null,
    lang: b.lang ?? null,
    // Carried, never defaulted. An absent `ownerId` must read as "we do not know who owns this", and
    // the predicate then answers no — which is the correct way for an access question to fail.
    ownerId: typeof b.ownerId === "string" ? b.ownerId : null,
    visibility: b.visibility ?? null,
    takenDownAt: typeof b.takenDownAt === "number" ? b.takenDownAt : null,
    deletedAt: typeof b.deletedAt === "number" ? b.deletedAt : null,
  };
}

function asChapters(body: unknown): Chapter[] {
  const c = unwrap(body, "chapters");
  if (!Array.isArray(c)) throw new Error("the table of contents was not a list of chapters");
  return c as Chapter[];
}

function asChapterPayload(body: unknown): ChapterPayload {
  const o = (body ?? {}) as Record<string, unknown>;
  const blocks = (Array.isArray(o["blocks"]) ? o["blocks"] : null) as Block[] | null;
  const chapter = (o["chapter"] ?? null) as Chapter | null;
  if (!blocks) throw new Error("the chapter came back with no blocks");
  if (!chapter || typeof chapter.ord !== "number") throw new Error("the chapter came back with no chapter record");
  const startOrd = typeof o["startOrd"] === "number" ? (o["startOrd"] as number) : null;
  return { chapter, blocks, startOrd };
}

/** `GET /books/:id/start` — "saved position, else bodyStart". */
function asStart(body: unknown): { sid: string | null; position: StoredPosition | null } {
  const o = (unwrap(body, "start") ?? {}) as Record<string, unknown>;
  const nested = (o["position"] ?? null) as Partial<StoredPosition> | null;
  const pos =
    nested && typeof nested.sid === "string" && typeof nested.editionId === "string"
      ? (nested as StoredPosition)
      : typeof o["sid"] === "string" && typeof o["editionId"] === "string"
        ? (o as unknown as StoredPosition)
        : null;
  const sid = typeof o["sid"] === "string" ? (o["sid"] as string) : (pos?.sid ?? null);
  return { sid, position: pos };
}

/* ══════════════════════════════════════════════════════════════════════════
 * Chapter parts
 * ══════════════════════════════════════════════════════════════════════════ */

interface LoadedChapter {
  chapter: Chapter;
  blocks: Block[];
  /** The mountable slices. §5.1's hard splits, or one slice for a normal chapter. */
  parts: Block[][];
  ordIndex: OrdIndex;
}

/**
 * §5.1's layout cost guard. "`scrollWidth` forces layout of the entire track. The server declares
 * hard splits: `parts: number[]` (block indices), computed at ingest whenever
 * `chapter.chars > 60_000` ... 60k chars ≈ a chapter longer than 95% of chapters, so the seam is
 * *rare* (one short page every ~40) rather than *regular*."
 *
 * Only the current part is mounted. Other chapters are not in the DOM at all — which is what keeps
 * the forced layout bounded no matter how long the book is.
 */
function splitParts(chapter: Chapter, blocks: Block[]): Block[][] {
  const cuts = (chapter.parts ?? [])
    .filter((n) => Number.isInteger(n) && n > 0 && n < blocks.length)
    .sort((a, b) => a - b);
  if (cuts.length === 0) return [blocks];

  const out: Block[][] = [];
  let prev = 0;
  for (const c of cuts) {
    out.push(blocks.slice(prev, c));
    prev = c;
  }
  out.push(blocks.slice(prev));
  return out.filter((p) => p.length > 0);
}

type PartSelector =
  | { kind: "index"; i: number }
  | { kind: "last" }
  /** Land in whichever part holds this block — how a sid navigation resolves. */
  | { kind: "block"; blockId: string };

function resolvePart(sel: PartSelector, lc: LoadedChapter | null): number {
  if (!lc) return 0;
  const n = lc.parts.length;
  switch (sel.kind) {
    case "last":
      return n - 1;
    case "block": {
      const i = lc.parts.findIndex((part) => part.some((b) => b.id === sel.blockId));
      return i < 0 ? 0 : i;
    }
    default:
      return Math.max(0, Math.min(n - 1, sel.i));
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * The component
 * ══════════════════════════════════════════════════════════════════════════ */

type Phase =
  | { k: "loading" }
  | { k: "error"; headline: string; detail: string }
  | { k: "ready" };

export function Reader(props: ReaderProps): ReactElement {
  const {
    bookId,
    startSid = null,
    onExit,
    narration = null,
    reactionRoomCode = null,
    onRoomReaction = null,
  } = props;

  // **State, not refs, for the four elements.** A ref assignment does not re-render, so a hook
  // that attaches listeners to `frameRef.current` would run once with `null` and never again —
  // the swipe and the tap zones would simply never work, with nothing in the console. Callback
  // refs into state cost one extra render on mount and make the dependency arrays honest.
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);
  const [frameEl, setFrameEl] = useState<HTMLDivElement | null>(null);
  const [trackEl, setTrackEl] = useState<HTMLDivElement | null>(null);
  const [probeEl, setProbeEl] = useState<HTMLSpanElement | null>(null);
  const [paginator, setPaginator] = useState<Paginator | null>(null);

  const geometryRef = useRef<Geometry | null>(null);
  const writerRef = useRef<PositionWriter | null>(null);
  const chapterCache = useRef<Map<number, LoadedChapter>>(new Map());
  const pendingLanding = useRef<Landing | null>(null);

  const typo = useTypography();

  const [phase, setPhase] = useState<Phase>({ k: "loading" });
  const [book, setBook] = useState<ReaderBook | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [chapterOrd, setChapterOrd] = useState<number | null>(null);

  /**
   * One id per mounted reader, for `listen_events.sessionId`.
   *
   * §13.1's falsifier is a **p95 per reading session on a real device**, not a p95 over the whole
   * table: one slow cold mount on a laptop and a thousand fast relayouts on a phone are different
   * populations, and without a session key they are one number that answers neither question.
   * `useState` with an initialiser rather than `useMemo` — a memo may legally be recomputed, and a
   * session id that changes mid-session is worse than none.
   */
  const [readerSessionId] = useState(() => randomId("rs_", 8));
  const [partSel, setPartSel] = useState<PartSelector>({ kind: "index", i: 0 });
  const [loaded, setLoaded] = useState<LoadedChapter | null>(null);

  const [column, setColumn] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [pinSid, setPinSid] = useState<string | null>(null);
  const [viewportTick, setViewportTick] = useState(0);
  const viewportCause = useRef<RepaginateCause>("mount");

  // The two values that a relayout must consult but must NEVER be re-triggered by. In P2
  // `spokenSid` changes on every sentence — putting it in a dependency array would repaginate the
  // book several times a minute, forever. A ref updated during render is the standard "latest
  // value" pattern and is safe here because nothing reads it during rendering.
  const live = useRef<{ spokenSid: string | null; following: boolean }>({ spokenSid: null, following: true });

  /**
   * LAW 12's flag, as a ref, because `usePageTurn` produces it and `useCueSync` consumes it while
   * `useCueSync` produces the `narrationColumn` that `usePageTurn` consumes. A value prop either way
   * round is a cycle; a ref written during render breaks it with no effect and no stale closure —
   * and it must be a ref rather than a value even setting the cycle aside, because the cue driver
   * reads it inside a `setTimeout` armed up to a page-turn earlier.
   */
  const followingRef = useRef(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [chromeHidden, setChromeHidden] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const partIndex = useMemo(() => resolvePart(partSel, loaded), [partSel, loaded]);
  const mounted = loaded?.parts[partIndex] ?? [];

  /**
   * Which mounted blocks belong to a hard-wrapped run — one sentence the source broke across
   * several `<p>`s. **The same predicate the narration uses to pick the pause** (`shared/text/
   * flow.ts`, imported by `server/src/narration/plan.ts`), so what the page joins is exactly what
   * the voice runs together. Two copies of this rule would drift into a read-along that shows a
   * paragraph the voice reads as two, with no error anywhere.
   *
   * A block is flagged if it continues the previous one **or** the next one continues it — both
   * ends of a run must go `display: inline`, because CSS only merges adjacent inline siblings into
   * one anonymous block box. Marking only the continuations would leave the first line stranded as
   * its own paragraph with the rest joined underneath it.
   */
  const flowed = useMemo(() => {
    const out = new Array<boolean>(mounted.length).fill(false);
    for (let i = 1; i < mounted.length; i++) {
      const prev = mounted[i - 1]!;
      const next = mounted[i]!;
      if (!continuesSentence(prev.kind, next.kind, prev.text, next.text)) continue;
      out[i - 1] = true;
      out[i] = true;
    }
    return out;
  }, [mounted]);

  /** Reading order, not spine order. `readOrd` and `compareOrdered` — the only orderer. */
  const readingOrder = useMemo(
    () =>
      [...chapters].sort((a, b) =>
        compareOrdered({ ord: a.readOrd, id: a.id }, { ord: b.readOrd, id: b.id }),
      ),
    [chapters],
  );

  /* ── Load the book ─────────────────────────────────────────────────────── */
  useEffect(() => {
    const ac = new AbortController();

    void (async () => {
      try {
        const [bookBody, tocBody] = await Promise.all([
          getJson(`/api/v1/books/${encodeURIComponent(bookId)}`, ac.signal),
          getJson(`/api/v1/books/${encodeURIComponent(bookId)}/chapters`, ac.signal),
        ]);
        if (ac.signal.aborted) return;

        const b = asBook(bookBody);
        const toc = asChapters(tocBody);
        if (toc.length === 0) throw new Error("this edition has no chapters");

        // `/start` is "saved position, else bodyStart" (§10); `/position` is the saved position on
        // its own, and it is fetched too because `/start` is allowed to answer with only a
        // `bodyStart` sid. Both may fail without taking the book down with them: a reader who is
        // offline, or whose position row has not been written yet, still gets to read.
        let start: { sid: string | null; position: StoredPosition | null } = { sid: null, position: null };
        let saved: StoredPosition | null = null;
        const [startRes, posRes] = await Promise.allSettled([
          getJson(`/api/v1/books/${encodeURIComponent(bookId)}/start`, ac.signal),
          fetchPositions(bookId, ac.signal),
        ]);
        if (ac.signal.aborted) return;
        if (startRes.status === "fulfilled") start = asStart(startRes.value);
        // Read or listen, whichever is newer: an hour of listening with the screen locked moves only
        // `listen` (written by the audio engine), and opening at the last page TURNED would put the
        // reader an hour behind the voice. `/start` makes the same choice on the server.
        if (posRes.status === "fulfilled") saved = newestPlace(posRes.value.read, posRes.value.listen, b.currentEditionId);

        const opening = chooseOpening({
          hashSid: sidFromHash(),
          propSid: startSid,
          server: { sid: start.sid, position: start.position ?? saved },
          local: newestPlace(loadLocal(bookId, "read"), loadLocal(bookId, "listen"), b.currentEditionId),
          editionId: b.currentEditionId,
          readingOrder: [...toc].sort((x, y) =>
            compareOrdered({ ord: x.readOrd, id: x.id }, { ord: y.readOrd, id: y.id }),
          ),
        });

        setBook(b);
        setChapters(toc);
        setNotice(opening.notice);
        setChapterOrd(opening.chapterOrd);
        setPartSel(opening.sid ? { kind: "block", blockId: blockIdFrom(opening.sid) ?? "" } : { kind: "index", i: 0 });
        pendingLanding.current = opening.sid ? { kind: "sid", sid: opening.sid } : { kind: "start" };
        setPhase({ k: "ready" });
      } catch (err) {
        if (ac.signal.aborted) return;
        setPhase(describeFailure(err));
      }
    })();

    return () => ac.abort();
  }, [bookId, startSid]);

  /* ── Load the current chapter ──────────────────────────────────────────── */
  useEffect(() => {
    if (chapterOrd == null) return;

    const cached = chapterCache.current.get(chapterOrd);
    if (cached) {
      setLoaded(cached);
      return;
    }

    const ac = new AbortController();
    void (async () => {
      try {
        const payload = asChapterPayload(
          await getJson(
            `/api/v1/books/${encodeURIComponent(bookId)}/chapters/${chapterOrd}`,
            ac.signal,
          ),
        );
        if (ac.signal.aborted) return;
        const lc: LoadedChapter = {
          chapter: payload.chapter,
          blocks: payload.blocks,
          parts: splitParts(payload.chapter, payload.blocks),
          ordIndex: buildOrdIndex(payload.blocks, payload.startOrd),
        };
        chapterCache.current.set(chapterOrd, lc);
        setLoaded(lc);
      } catch (err) {
        if (ac.signal.aborted) return;
        setPhase(describeFailure(err));
      }
    })();

    return () => ac.abort();
  }, [bookId, chapterOrd]);

  /* ── The position writer ───────────────────────────────────────────────── */
  useEffect(() => {
    const w = new PositionWriter(bookId);
    writerRef.current = w;
    return () => {
      w.beacon();
      w.dispose();
      writerRef.current = null;
    };
  }, [bookId]);

  /* ── `--vvh`: the visual viewport, in pixels ───────────────────────────── */
  useEffect(() => {
    const root = rootEl;
    if (!root) return;

    let last = -1;
    const set = () => {
      const vv = typeof visualViewport !== "undefined" ? visualViewport : null;
      // **Multiplied by `scale`.** A pinch-zoom shrinks `visualViewport.height` by the zoom
      // factor, and writing that through would repaginate the book under the reader's fingers —
      // on the one gesture §5.2 pitfall (3) deliberately kept working for the people who need it
      // most. Multiplying it back out yields the layout viewport, which is what the frame wants
      // and which is stable under pinch and correct across a rotation or a keyboard.
      const h = vv ? vv.height * vv.scale : innerHeight;
      const px = Math.round(h);
      if (Math.abs(px - last) < 2) return;
      last = px;
      root.style.setProperty("--vvh", `${px}px`);
    };
    set();

    const vv = typeof visualViewport !== "undefined" ? visualViewport : null;
    vv?.addEventListener("resize", set);
    addEventListener("resize", set);
    addEventListener("orientationchange", set);
    return () => {
      vv?.removeEventListener("resize", set);
      removeEventListener("resize", set);
      removeEventListener("orientationchange", set);
    };
  }, [rootEl]);

  /* ── The Paginator, and the viewport observer ──────────────────────────── */
  useEffect(() => {
    if (!frameEl || !trackEl || !stageEl) return;

    const p = new Paginator(frameEl, trackEl);
    setPaginator(p);

    const off = onViewportChange(stageEl, (cause) => {
      viewportCause.current = cause;
      setViewportTick((t) => t + 1);
    });

    return () => {
      off();
      p.destroy();
      setPaginator(null);
    };
  }, [frameEl, trackEl, stageEl]);

  /* ── Typography + geometry, the only style write in a relayout ─────────── */
  const applyTypography = useCallback(() => {
    const root = rootEl;
    const frame = frameEl;
    const stage = stageEl;
    const p = paginator;
    if (!root || !frame || !stage || !p) return;

    typo.applyTo(root);

    // Reading the probe forces a style+layout flush of its own, before the `scrollWidth` read in
    // Paginator.measure(). That is two flushes per relayout rather than §5.3's literal one, and it
    // is unavoidable: `ch` is a font metric, so the only way to know the column width in the face
    // actually in use is to let the browser resolve the unit. It is included in the reported `ms`
    // rather than hidden, because the number that matters is how long a font-size change takes,
    // not how long one of its two flushes takes.
    const cs = getComputedStyle(frame);
    const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const gutter = parseFloat(cs.columnGap) || 32;
    const m = readFontMetrics(probeEl);

    const g = geometryFor({
      outerW: stage.clientWidth,
      outerH: stage.clientHeight,
      padX,
      padY,
      chPx: m.chPx,
      emPx: m.emPx,
      gutter,
      measureCh: m.measureCh,
      measureMaxEm: m.measureMaxEm,
      spreadPref: typo.prefs.spread,
    });
    p.applyGeometry(g);
    geometryRef.current = g;

    // §5.8: leading keys off the COLUMN. A 380 px column on a MacBook in spread mode is narrow,
    // and no viewport media query can know that.
    frame.dataset.measure = measureBucket(g.pageW / Math.max(1, m.chPx));
  }, [typo.applyTo, typo.prefs.spread, rootEl, frameEl, stageEl, probeEl, paginator]);

  // A theme change moves no type and needs no relayout, so it must not wait for one. This effect
  // is the only place typography is applied *outside* the repaginate sequence, and it is safe
  // precisely because it is idempotent: repaginate calls the same function a moment later with
  // the same values and nothing moves.
  useEffect(() => {
    typo.applyTo(rootEl);
  }, [typo.applyTo, rootEl]);

  /* ── Position ──────────────────────────────────────────────────────────── */
  const recordPosition = useCallback(
    (sid: string | null) => {
      const w = writerRef.current;
      const ed = book?.currentEditionId;
      if (!w || !sid || !ed || !loaded) return;
      const ord = loaded.ordIndex.ordOf(sid);
      if (ord === undefined) return;
      // LAW 1: the sid goes to the server with the editionId it is meaningful in, always.
      w.update(makePosition({ sid, editionId: ed, ord, kind: "read" }));
    },
    [book?.currentEditionId, loaded],
  );

  /**
   * "The voice has walked off this screen — go to it."
   *
   * A ref because `useCueSync` runs above `goToSid` in this file and `goToSid` needs the paginator
   * and the loaded chapter, both of which are established below it. A `useCallback` forward-declared
   * here would either capture stale state or force the driver to be rebuilt on every relayout, and
   * rebuilding the driver drops the highlight and every armed turn.
   */
  const followNarration = useRef<((sid: string) => void) | null>(null);

  /* ── The read-along ────────────────────────────────────────────────────── */
  const cue = useCueSync({
    page: narration?.page ?? null,
    clock: narration?.clock ?? null,
    playing: narration?.playing === true,
    track: trackEl,
    paginator,
    following: followingRef,
    onEvent: narration?.onEvent ?? null,
    frames: narration?.frames ?? null,
    onOffScreen: (sid) => followNarration.current?.(sid),
  });

  // `runRepaginate` is a `useCallback` and must not take `cue` as a dependency: `cue` is a fresh
  // object on every highlight change, i.e. once a sentence, and a relayout dependency that changes
  // once a sentence would repaginate the book several times a minute forever. Same reason as `live`.
  const cueRef = useRef(cue);
  cueRef.current = cue;

  /* ── The relayout ──────────────────────────────────────────────────────── */
  const prevRun = useRef({ key: "", sig: "", fonts: false, started: false });

  const runRepaginate = useCallback(
    (cause: RepaginateCause) => {
      const p = paginator;
      if (!p) return;

      const landing = pendingLanding.current ?? { kind: "pin" };
      pendingLanding.current = null;

      const r = repaginate(
        {
          paginator: p,
          // §5.3's `ctx.spokenSid ?? ctx.topVisibleSid()` — **but only while following.**
          //
          // ⚠️ LAW 12 / §5.5: "Auto-refollow when the user lands back on the narration's page, or
          // after 20 s idle with that page displayed. **Never by moving the user.**" A reader who
          // has flipped ahead to check a name and then changes the font size must land on the page
          // they are *looking at*, not be dragged back to the voice. Pinning `spokenSid`
          // unconditionally does exactly that, silently, on every relayout — which makes flipping
          // ahead while listening impossible, i.e. it deletes the interaction LAW 12 exists to
          // protect. Caught in the Chrome harness; the turn landed and then jumped back.
          spokenSid: live.current.following ? live.current.spokenSid : null,
          landing,
          applyTypography,
          // Typography only. repaginate() appends the geometry AFTER applyTypography has run —
          // this callback cannot, because at this point `geometryRef` still describes the layout
          // that is about to be replaced.
          typographySignature: typo.signature,
          // ⚠️ `bookId`/`editionId` are REQUIRED by the route, not optional context. The beacon
          // without them is a 404 that `beaconLayout` swallows by design, so §13.1's entire
          // instrumentation reported nothing and looked like it was working. See
          // `LayoutTelemetryInput`.
          telemetry: {
            scale: typo.scale,
            font: typo.prefs.font,
            bookId,
            editionId: book?.currentEditionId ?? "",
            sessionId: readerSessionId,
            /**
             * ⚠️ **The chapter that is MOUNTED, not the chapter that was requested.**
             *
             * `chapterOrd` is state and it changes the moment navigation starts; `loaded` is the
             * chapter whose blocks are actually in the DOM and it lags by one fetch. In between,
             * `useLayoutEffect` fires and measures the OLD chapter's track — labelling it with the
             * NEW chapter's number. That is a lie in the telemetry, and because `repaginate` keys the
             * instability check on (chapter, part), it is a lie that FIRES THE FALSIFIER: chapter 9's
             * 11 columns get filed under chapter 10, then chapter 10 really mounts at 13, and the
             * beacon reports a `scrollWidth` instability at identical typography. Observed exactly
             * once in a 45-beacon Chromium run, which is how a real instability would look too — and
             * that is the whole problem with a falsifier that cries wolf.
             */
            chapterOrd: loaded?.chapter.ord ?? chapterOrd ?? 0,
            // Same reason: the key must name the text that was measured.
            part: partIndex,
          },
        },
        cause,
      );

      setColumn(r.column);
      setPageCount(p.pageCount);
      const sid = p.sidAtColumn(r.column) ?? r.pin;
      setPinSid(sid ?? null);
      recordPosition(sid ?? null);

      // ⚠️ **The other half of the acceptance test.** §5.3's gate is "change the font size mid-
      // narration: the page reflows, the correct page is showing, **the highlight is still on the
      // spoken sentence**, and the audio does not seek or gap." The audio half falls out for free
      // because nothing ever told the audio about layout. The highlight half does not: `columnOf`
      // was just rebuilt from scratch, so every column this driver is holding — the one it turned
      // to, the one it has a timer armed for — describes a layout that no longer exists. One
      // synchronous resync, `animate: false`, and the wash is on the same sentence in its new place.
      cueRef.current.resync();
    },
    // `narration` is deliberately absent: it is read through `live` for the reason above.
    [
      applyTypography,
      paginator,
      recordPosition,
      typo.signature,
      typo.scale,
      typo.prefs.font,
      bookId,
      book?.currentEditionId,
      readerSessionId,
      chapterOrd,
      loaded?.chapter.ord,
      partIndex,
    ],
  );

  useLayoutEffect(() => {
    if (phase.k !== "ready" || !loaded || !paginator) return;

    const key = `${chapterOrd}:${partIndex}`;
    const prev = prevRun.current;
    let cause: RepaginateCause;
    if (!prev.started) cause = "mount";
    else if (prev.key !== key) cause = "part";
    else if (prev.fonts !== typo.fontsReady) cause = "fonts";
    else if (prev.sig !== typo.signature) cause = "typography";
    else cause = viewportCause.current;

    prevRun.current = { key, sig: typo.signature, fonts: typo.fontsReady, started: true };
    runRepaginate(cause);
  }, [phase.k, loaded, partIndex, chapterOrd, paginator, typo.signature, typo.fontsReady, viewportTick, runRepaginate]);

  /* ── Images settle after the first measure ─────────────────────────────── */
  useEffect(() => {
    const track = trackEl;
    if (phase.k !== "ready" || !track || mounted.length === 0) return;
    let cancelled = false;
    void imagesSettled(track).then(() => {
      // Only relayout if there was something to wait for. `imagesSettled` resolves synchronously
      // when every image is already complete, and a relayout on every part change for a text-only
      // book would double the cost of turning to a new chapter.
      if (!cancelled && track.querySelector("img")) runRepaginate("images");
    });
    return () => {
      cancelled = true;
    };
  }, [phase.k, loaded, partIndex, trackEl, runRepaginate, mounted.length]);

  /* ── Coming back to the tab ────────────────────────────────────────────── */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // §5.7: "On `visible`, **one synchronous resync before paint**: recompute the cue index from
      // `currentTime`, jump the highlight, jump the page with `animate:false`, restart the rAF. No
      // catching-up animation. Four lines, and it is the 'open in the back of a MacBook' requirement."
      //
      // Both halves run here, in this order, **synchronously inside the event handler** — not in a
      // rAF, not in an effect. A backgrounded tab runs no rAF at all and clamps `setTimeout` to a
      // second or more, so the reader comes back holding a cue index and an armed page turn from
      // however many minutes ago it was hidden. Deferring the correction by even one frame is what
      // produces the catching-up animation the design refuses: the old page paints once, then slides.
      //
      // Repagination first: the frame may have been resized while hidden (a rotation in another app,
      // a window resize behind the tab), and resyncing against a stale column map would jump the
      // highlight to a column that is about to be renumbered. `runRepaginate` ends by calling
      // `cue.resync()` itself, which is why there is no second call here.
      runRepaginate("visibility");
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [runRepaginate]);

  /* ── Navigation ────────────────────────────────────────────────────────── */
  const goToChapter = useCallback(
    (ord: number, landing: Landing, part: PartSelector) => {
      pendingLanding.current = landing;
      setPartSel(part);
      setChapterOrd(ord);
    },
    [],
  );

  const goToSid = useCallback(
    (sid: string) => {
      const ord = chapterOrdFrom(sid);
      const blockId = blockIdFrom(sid);
      if (ord === null || blockId === null) return;
      if (ord === chapterOrd && loaded) {
        const target = resolvePart({ kind: "block", blockId }, loaded);
        if (target === partIndex) {
          const p = paginator;
          const col = p?.columnForSid(sid);
          if (p && col !== undefined) {
            const landed = p.goTo(col);
            setColumn(landed);
            const pinned = p.sidAtColumn(landed) ?? sid;
            setPinSid(pinned);
            recordPosition(pinned);
            return;
          }
        }
      }
      goToChapter(ord, { kind: "sid", sid }, { kind: "block", blockId });
    },
    [chapterOrd, loaded, partIndex, paginator, goToChapter, recordPosition],
  );

  /**
   * ⚠️ **LAW 12, read forwards.** "Turning a page is browsing. It never moves the narration" — and
   * the converse is the feature: *the narration moves the page.* This is the only place the reader
   * changes what is mounted because of where the voice is, and it is gated on `following`, so a
   * listener who has flipped back to check a name keeps the pill and keeps their place.
   *
   * The mounted unit is ONE chapter and one `chapters.parts` slice of it; the audio walks the whole
   * book. Nothing connected the two, so at every chapter boundary the engine advanced correctly, the
   * lock screen updated correctly, and the screen stayed frozen on the last column of the chapter
   * before while `useCueSync` dropped three cues and switched the read-along off with a notice that
   * blamed the artifact. Alice's chapter 5 has seven pages, which is why P2's "four page boundaries"
   * passed over it.
   */
  const followToSid = useCallback(
    (sid: string) => {
      if (!followingRef.current) return;
      const ord = chapterOrdFrom(sid);
      const blockId = blockIdFrom(sid);
      if (ord === null || blockId === null) return;
      // Already mounted: `useCueSync` will resolve it on its own next frame, and navigating would
      // repaginate for nothing. This is the ordinary case at every page boundary inside a chapter.
      if (ord === chapterOrd && loaded && resolvePart({ kind: "block", blockId }, loaded) === partIndex) return;
      goToSid(sid);
    },
    [chapterOrd, loaded, partIndex, goToSid],
  );
  followNarration.current = followToSid;

  /**
   * The proactive half: act on the page the engine just opened rather than waiting for three cues to
   * be dropped.
   *
   * `pageHead` puts 620–900 ms of deliberate silence at the head of every page (§4.2), so a
   * navigation started here lands *before the first word*, which is the same budget §5.5's scheduled
   * turn is built around: the eye arrives before the voice. The drop path above stays as the safety
   * net for a page that starts inside the mounted part and runs off the end of it.
   */
  const narrationFirstSid = useMemo(() => {
    const check = validatePageAudio(narration?.page ?? null);
    return check.ok ? check.page.firstSid : null;
  }, [narration?.page]);

  /**
   * ⚠️ **It follows a page the engine just OPENED, not every render of this effect.**
   *
   * `followToSid` is rebuilt whenever `chapterOrd`, `loaded` or `partIndex` changes — that is, on
   * every navigation — so listing it as a dependency made this effect fire *because the reader had
   * just navigated*, with `narrationFirstSid` still naming the page the engine was holding. The
   * result: pick a chapter from the drawer, land on it, and be pulled straight back to the narrated
   * page, inside one frame, with no error and nothing in the console.
   *
   * It only bites once the engine holds a page, which is why P2 never saw it: in the plain reader
   * nothing is open until somebody presses Play, and by then a manual turn has usually cleared
   * `following`. A **room** opens the page for you on join (§7.4's load branch), so in a room the
   * chapter drawer was inert from the first second — measured in two real Chromes: chapter VIII
   * selected, `.ab-chapter-btn` clicked, anchor still `c005.b0000.s0` ten seconds later, six polls
   * apart. Same click on `/b/:id/read`: `c012.b0000.s0`.
   *
   * The ref is the whole fix. A sid is followed once, when it is new.
   */
  const followedSid = useRef<string | null>(null);
  useEffect(() => {
    if (narrationFirstSid === null || phase.k !== "ready") return;
    if (followedSid.current === narrationFirstSid) return;
    followedSid.current = narrationFirstSid;
    followToSid(narrationFirstSid);
  }, [narrationFirstSid, phase.k, followToSid]);

  /**
   * A turn ran off the end of the mounted part. Only this component knows what is next: another
   * part of the same chapter, the next chapter in *reading* order, or the end of the book.
   */
  const pendingReadAdvanceFrom = useRef<string | null>(null);
  const onOverflow = useCallback(
    (dir: -1 | 1, source: TurnSource) => {
      if (!loaded) return;
      const parts = loaded.parts.length;

      if (dir > 0 && partIndex + 1 < parts) {
        if (source !== "narration") pendingReadAdvanceFrom.current = pinSid;
        pendingLanding.current = { kind: "start" };
        setPartSel({ kind: "index", i: partIndex + 1 });
        return;
      }
      if (dir < 0 && partIndex > 0) {
        pendingLanding.current = { kind: "end" };
        setPartSel({ kind: "index", i: partIndex - 1 });
        return;
      }

      const here = readingOrder.findIndex((c) => c.ord === chapterOrd);
      const next = here < 0 ? -1 : here + dir;
      const target = next >= 0 && next < readingOrder.length ? readingOrder[next] : undefined;
      if (!target) return; // the front cover, or the last page of the book. Nothing to do.

      if (dir > 0 && source !== "narration") pendingReadAdvanceFrom.current = pinSid;

      goToChapter(
        target.ord,
        dir > 0 ? { kind: "start" } : { kind: "end" },
        dir > 0 ? { kind: "index", i: 0 } : { kind: "last" },
      );
    },
    [loaded, partIndex, readingOrder, chapterOrd, goToChapter, pinSid],
  );

  const onTurned = useCallback(
    (col: number, source: TurnSource, dir: -1 | 0 | 1) => {
      setColumn(col);
      const sid = paginator?.sidAtColumn(col) ?? null;
      setPinSid(sid);
      recordPosition(sid);
      if (dir > 0 && source !== "narration" && sid !== null) narration?.onReadAdvance?.(sid);
      // A book has no chrome. The first deliberate turn is the moment to get out of the way; a
      // centre tap brings it back.
      setChromeHidden(true);
    },
    [paginator, recordPosition, narration?.onReadAdvance],
  );

  /* ── P3: the sentence menu ─────────────────────────────────────────────── */

  /**
   * §5.6, on both input paths, attached to the **track** — one delegated listener that survives every
   * repagination rather than one per `[data-s]` span, of which a chapter has thousands.
   *
   * `selection` is declared first because the long-press opener must stand down while native selection
   * is restored: racing iOS's own callout is the one thing §5.6 says cannot be done.
   */
  const selection = useSelectionMode(trackEl);
  const menu = useSentenceMenu({
    track: trackEl,
    enabled: phase.k === "ready" && !drawerOpen && !sheetOpen && !searchOpen,
    selectionMode: selection,
  });

  const { actor } = useAuth();

  /**
   * The book, structurally, for `shared/access.ts` — **LAW 13**. `null` until the fetch lands, which
   * is what keeps the menu from briefly offering a regenerate row to somebody who cannot have one.
   */
  const accessBook = useMemo<(AccessBook & { id: string; currentEditionId?: string | null }) | null>(() => {
    if (book === null || book.ownerId == null || book.visibility == null) return null;
    return {
      id: book.id,
      ownerId: book.ownerId,
      visibility: book.visibility,
      takenDownAt: book.takenDownAt ?? null,
      deletedAt: book.deletedAt ?? null,
      grants: book.grants ?? undefined,
      currentEditionId: book.currentEditionId,
    };
  }, [book]);

  const mayRegenerate = accessBook !== null && canRegenerate(accessBook, actor);

  const turnApi = usePageTurn({
    frame: frameEl,
    paginator,
    // ⚠️ `menu.target === null` is not tidiness. Without it the transparent scrim's pointer events
    // reach the tap zones underneath, and dismissing the menu turns the page it was anchored to —
    // so the sentence you were fixing is no longer on screen when the fix lands.
    enabled: phase.k === "ready" && !drawerOpen && !sheetOpen && !searchOpen && menu.target === null && !selection.active,
    onOverflow,
    onTurned,
    onToggleChrome: () => setChromeHidden((h) => !h),
    // `useCueSync` first: it is measuring this layout. `narration.column` is the P1/P5 path — an
    // externally computed column, which a co-read follower has and this reader does not.
    narrationColumn: cue.narrationColumn ?? narration?.column ?? null,
    // Present only when there is something to play, which is what makes Space's meaning match what
    // is on screen rather than what is in the props. See the key handler in usePageTurn.
    onTogglePlay: narration?.onPlayPause ?? null,
  });

  const spokenSid = cue.spokenSid ?? narration?.spokenSid ?? null;
  live.current = { spokenSid, following: turnApi.following };
  followingRef.current = turnApi.following;

  // Room chrome and the reader must own one follow flag. A local page turn publishes false through
  // `onFollowingChange`; a visible Follow/Resync action drives the same flag back to true here.
  useEffect(() => {
    if (typeof narration?.following === "boolean") turnApi.setFollowing(narration.following);
  }, [narration?.following, turnApi.setFollowing]);

  // Unlike `spokenSid`, this request is navigation, not a repagination preference. The sequence is
  // load-bearing: after browsing away, "Back to the room" often targets the same room sid again.
  const followedRequest = useRef<number | null>(null);
  useEffect(() => {
    const request = narration?.followRequest ?? null;
    if (request === null || phase.k !== "ready" || followedRequest.current === request.seq) return;
    followedRequest.current = request.seq;
    turnApi.setFollowing(true);
    goToSid(request.sid);
  }, [narration?.followRequest, phase.k, goToSid, turnApi.setFollowing]);

  /**
   * LAW 12's flag, published. See `ReaderNarration.onFollowingChange` for what its absence cost.
   *
   * ⚠️ **In an effect, not on the line above.** `followingRef.current = …` runs during render, and a
   * subscriber that calls `setState` from there is a React "cannot update a component while
   * rendering a different component" warning at best and a render loop at worst — and the one caller
   * is `Room.tsx`, which does exactly that. The effect fires only when the value actually changed.
   */
  const onFollowingChange = narration?.onFollowingChange ?? null;
  useEffect(() => {
    onFollowingChange?.(turnApi.following);
  }, [onFollowingChange, turnApi.following]);

  /* ── Derived display state ─────────────────────────────────────────────── */
  // Read off the Paginator rather than mirrored into state: `pageCount` and `column` are already
  // state and their updates are what re-render this component, so these two reads are always
  // fresh, and keeping the turn arithmetic in one place (spread mode makes one turn two columns)
  // means the footer cannot disagree with the transform about what page it is.
  const turnsTotal = paginator ? paginator.turnCount() : Math.max(1, pageCount);
  const turnNow = paginator ? paginator.turnIndex() + 1 : 1;

  const charsLeft = useMemo(() => {
    if (!loaded || !pinSid) return null;
    const blockId = blockIdFrom(pinSid);
    if (!blockId) return null;
    const i = loaded.blocks.findIndex((b) => b.id === blockId);
    if (i < 0) return null;
    let n = 0;
    for (let k = i; k < loaded.blocks.length; k++) n += loaded.blocks[k]!.text.length;
    return n;
  }, [loaded, pinSid]);

  const imageSrc = useCallback(
    (sha: string) => `/api/v1/books/${encodeURIComponent(bookId)}/images/${encodeURIComponent(sha)}`,
    [bookId],
  );

  /**
   * Tell the narration plug which sentence a press of play should start from.
   *
   * `pinSid` is the top-visible sentence of the landed page and is already what `repaginate` pins to,
   * so this costs nothing to compute and changes a handful of times a minute. It is the only thing the
   * audio engine cannot work out for itself: it holds no DOM and no column map by design, and a saved
   * position is stale the moment somebody turns a page.
   *
   * Not in the render body and not a `useMemo`: a callback that fires during render would call into
   * the plug's ref while React is reconciling, which is exactly the "write during render" this file
   * uses `live` and `followingRef` to avoid everywhere else.
   */
  const onAnchor = narration?.onAnchor ?? null;
  useEffect(() => {
    onAnchor?.(pinSid);
    if (pinSid !== null && pendingReadAdvanceFrom.current !== null && pendingReadAdvanceFrom.current !== pinSid) {
      pendingReadAdvanceFrom.current = null;
      narration?.onReadAdvance?.(pinSid);
    }
  }, [onAnchor, pinSid, narration?.onReadAdvance]);

  const visibleEndSid = useMemo(() => {
    const p = paginator;
    if (p === null || loaded === null) return null;
    const firstCol = p.page;
    const afterCol = firstCol + (p.geometry?.colsPerTurn ?? 1);
    let best: { sid: string; ord: number } | null = null;
    for (const [sid, col] of p.columnOf) {
      if (col < firstCol || col >= afterCol) continue;
      const ord = loaded.ordIndex.ordOf(sid);
      if (ord !== undefined && (best === null || ord > best.ord)) best = { sid, ord };
    }
    return best?.sid ?? pinSid;
  }, [paginator, loaded, column, pinSid]);
  const onVisibleEnd = narration?.onVisibleEnd ?? null;
  useEffect(() => {
    onVisibleEnd?.(visibleEndSid);
  }, [onVisibleEnd, visibleEndSid]);

  /* ── Render ────────────────────────────────────────────────────────────── */
  return (
    <div className="ab-reader" ref={setRootEl} data-chrome={chromeHidden ? "hidden" : "shown"}>
      <div className="ab-topbar">
        {onExit ? (
          <button type="button" className="ab-btn" onClick={onExit} aria-label="Back to the shelf">
            ←
          </button>
        ) : null}
        <span className="ab-bar-title">{book?.title ?? " "}</span>
        <button
          type="button"
          className="ab-btn"
          onClick={() => setDrawerOpen(true)}
          aria-label="Chapters"
          disabled={phase.k !== "ready"}
        >
          Chapters
        </button>
        <button
          type="button"
          className="ab-btn"
          onClick={() => setSearchOpen(true)}
          aria-label="Search this book"
          disabled={phase.k !== "ready"}
        >
          Find
        </button>
        <button
          type="button"
          className="ab-btn"
          onClick={() => setSheetOpen((s) => !s)}
          aria-label="Reading settings"
          aria-pressed={sheetOpen}
          disabled={phase.k !== "ready"}
        >
          Aa
        </button>
      </div>

      {notice ? (
        <div className="ab-notice" role="status">
          <span>{notice}</span>
          <button type="button" className="ab-btn" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="ab-stage" ref={setStageEl}>
        {/* The `ch` probe. Outside .pf-track deliberately — an extra element inside a
            fragmentation context occupies column space and adds a phantom page. */}
        <span className="read ab-ch-probe" ref={setProbeEl} aria-hidden="true" />

        <div className="pf-frame" ref={setFrameEl}>
          {/* `.pf-clip` is not decoration and it is not a styling hook — it is the box that clips
              the columns, and it is a separate element because it must be exactly `--track-w` wide
              while the frame must be 100%. Removing it repaints the previous and next page into the
              margins at every viewport between one column and a spread. See reader-page.css. */}
          <div className="pf-clip">
            <div
              className="pf-track read"
              ref={setTrackEl}
              data-justify={typo.prefs.justify ? "1" : undefined}
              lang={book?.lang ?? undefined}
            >
              {mounted.map((block, i) =>
                renderBlock(block, {
                  imageSrc,
                  // The chapter's own title carries the drop cap. Only the first heading of the
                  // first part: a drop cap after every section heading reads as a magazine.
                  chapterTitle: partIndex === 0 && i === 0 && block.kind === "heading",
                  flow: flowed[i],
                }),
              )}
            </div>
          </div>
        </div>

        {phase.k === "loading" ? (
          <div className="ab-state" role="status">
            <span>Opening the book…</span>
          </div>
        ) : null}

        {phase.k === "error" ? (
          <div className="ab-state" role="alert">
            <strong>{phase.headline}</strong>
            <span>{phase.detail}</span>
          </div>
        ) : null}

        {/* LAW 12's pill. Rendered only when the voice is on a page the reader is not on — so it
            never offers to take anybody where they already are, and it never appears at all in a
            book with no narration. */}
        {turnApi.showFollowPill && turnApi.narrationTurn !== null ? (
          <FollowPill
            page={turnApi.narrationTurn}
            voice={narration?.voice ?? "Karti"}
            onBack={turnApi.backToNarration}
          />
        ) : null}

        <ChapterDrawer
          open={drawerOpen}
          chapters={chapters}
          currentOrd={chapterOrd}
          onSelect={(ord) => {
            // Choosing a chapter is a deliberate departure, exactly like turning a page, and it has
            // to clear `following` for the same reason — otherwise the voice's own page pulls the
            // reader back and the drawer reads as broken. §5.5's pill is what offers the way back.
            turnApi.stopFollowing();
            goToChapter(ord, { kind: "start" }, { kind: "index", i: 0 });
          }}
          onClose={() => setDrawerOpen(false)}
        />

        {/* ⚠️ LAW 12, and LAW 1. A search result is a place to LOOK, so landing on one clears
            `following` exactly as choosing a chapter does — the voice keeps reading and the follow
            pill offers the way back. And the panel is handed `currentEditionId`: `goToSid` parses a
            sid positionally, so a hit from another edition would resolve to a different sentence
            that happens to exist rather than fail, and the panel refuses instead of jumping. */}
        <SearchPanel
          open={searchOpen}
          bookId={bookId}
          editionId={book?.currentEditionId ?? null}
          onJump={(sid) => {
            turnApi.stopFollowing();
            goToSid(sid);
          }}
          onClose={() => setSearchOpen(false)}
        />

        {sheetOpen ? (
          <TypographySheet typo={typo} onClose={() => setSheetOpen(false)} />
        ) : null}
      </div>

      {/* ⚠️ **Not inside `[data-chrome="hidden"]`.** The topbar and the footer hide on the first
          turn because a book has no chrome; the transport does not, because a control you cannot
          find while the voice is talking is a control people solve by reloading the page. It is
          rendered only when a narration exists, so an un-narrated book is byte-for-byte P1's
          layout — including the measured `--frame-h`, which is what would otherwise repaginate. */}
      {narration ? (
        <NarrationChrome
          narration={narration}
          cueNotice={cue.notice}
          chapterTitle={loaded?.chapter.title ?? ""}
        />
      ) : null}

      <ProgressFooter
        page={turnNow}
        pageCount={turnsTotal}
        chapterTitle={loaded?.chapter.title ?? ""}
        partOf={
          loaded && loaded.parts.length > 1
            ? { index: partIndex + 1, count: loaded.parts.length }
            : null
        }
        charsLeftInChapter={charsLeft}
        dense={book?.narrationStyle === "dense" || book?.narrationStyle === "scripture"}
      />

      {/* ⚠️ **Outside `.ab-stage`, and that is structural.** Both of these are `position: fixed`, and
          the stage's track carries a `translate3d` — a transformed ancestor becomes the containing
          block for a fixed descendant, so mounted in there the menu would be positioned against the
          page track and would slide sideways with every page turn. Rendered here it is positioned
          against the viewport, which is what its measure-then-flip code assumes. */}
      <SentenceMenu
        target={menu.target}
        book={accessBook}
        actor={actor}
        voiceId={narration?.voiceId ?? null}
        roomCode={reactionRoomCode}
        onRoomReaction={onRoomReaction}
        onClose={menu.close}
        onPlayFrom={(sid) => narration?.onPlayFrom?.(sid)}
        onPauseReader={narration?.playing === true ? () => narration.onPlayPause?.() : null}
        // Only a coarse pointer needs it: on a desktop native selection already works, and offering
        // "Select text" there would be a row that does nothing anybody can see.
        onSelectText={
          typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches
            ? () => selection.enter()
            : null
        }
        onRetry={narration?.onRetry ?? null}
        failed={narration?.state === "failed"}
        onNotice={setNotice}
        onChanged={(r) => narration?.onChanged?.(r)}
      />

      {/* Its own state boundary is intentional: a room refreshes marks every ten seconds, but the
          thousands of rendered sentence spans above must not reconcile on that clock. It mutates only
          namespaced data/ARIA attributes and its pseudo-elements are outside the column flow. */}
      <PersistentReactionMarks
        track={trackEl}
        bookId={bookId}
        editionId={book?.currentEditionId ?? null}
        chapterOrd={loaded?.chapter.ord ?? chapterOrd}
        roomCode={reactionRoomCode}
      />

      <SelectionChip
        mode={selection}
        // LAW 13 again, in the shape the chip asks for: **absent** when the predicate says no, never
        // present and disabled. There is nothing here for a stale `disabled` prop to lie about.
        onRegenerate={
          mayRegenerate
            ? (sids) => {
                selection.exit();
                menu.openOn(sids, { x: window.innerWidth / 2, y: window.innerHeight / 2 }, true);
              }
            : null
        }
        onReport={(sids) => {
          selection.exit();
          menu.openOn(sids, { x: window.innerWidth / 2, y: window.innerHeight / 2 }, true);
        }}
      />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * The transport
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * `PlaybackBar` + `Scrubber`, and **the reason they are wrapped in a component of their own.**
 *
 * A scrubber has to move while the audio plays, which means something has to re-render on a timer.
 * If that something is `<Reader>`, the timer re-renders `mounted.map(renderBlock)` — up to a couple
 * of thousand `<span data-s>` elements — several times a second, forever, on a phone, with the
 * screen off. React would reconcile them to nothing, which is precisely what makes the cost
 * invisible: no flicker, no repaint, no error, just a CPU floor under a feature whose whole selling
 * point is that you can leave it running. Confining the tick to this subtree is four lines and it is
 * the difference between "it kept reading for four page boundaries" and "the battery died".
 *
 * Twice a second, and only while playing. A scrubber on a twenty-minute chapter advances 0.04% of
 * its width per tick; 60 Hz would buy nothing a person can see. The *highlight* is the thing that
 * needs 60 Hz, and it never goes through React at all — `useCueSync` swaps one class on one element.
 */
function NarrationChrome(props: {
  narration: ReaderNarration;
  cueNotice: string | null;
  chapterTitle: string;
}): ReactElement {
  const { narration, cueNotice, chapterTitle } = props;
  const playing = narration.playing === true;
  const [, tick] = useState(0);
  const [listenOpen, setListenOpen] = useState(false);
  const closeListen = useCallback(() => setListenOpen(false), []);
  const listen = narration.listen ?? null;

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(id);
  }, [playing]);

  const el = narration.clock?.el ?? null;
  const inPageMs = el && Number.isFinite(el.currentTime) ? el.currentTime * 1000 : 0;
  const chapter = narration.chapter ?? null;
  const positionMs = (chapter?.pageOffsetMs ?? 0) + inPageMs;
  const chapterMs = chapter?.durationMs ?? 0;
  const leftMs = chapter && chapterMs > positionMs ? chapterMs - positionMs : null;

  /**
   * LAW 3, at the one place a millisecond is allowed to become a position.
   *
   * A scrub inside the loaded page is resolved against **our own cue list** — `{sid, frac}` — so the
   * engine seeks to a sentence rather than to a number that describes a slightly different artifact.
   * A scrub outside it is a page change, and this reader has no book timeline: it hands the
   * millisecond back and says so, rather than guessing which page owns it and being confidently
   * wrong by a page every time a chapter's clips are regenerated.
   */
  const onScrub = (chapterTargetMs: number): void => {
    const check = validatePageAudio(narration.page);
    const offset = chapter?.pageOffsetMs ?? 0;
    const inPage = chapterTargetMs - offset;
    if (check.ok && narration.onSeek && inPage >= 0 && inPage <= check.page.durationMs) {
      const at = seekTargetInPage(check.page, inPage);
      if (at) {
        narration.onSeek(at);
        return;
      }
    }
    narration.onSeekChapterMs?.(chapterTargetMs);
  };

  return (
    <div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column" }}>
      {(narration.state ?? "missing") === "ready" ? (
        <PlaybackBar
          state="ready"
          playing={playing}
          rate={narration.clock?.playbackRate ?? 1}
          chapterTitle={chapterTitle}
          msLeftInChapter={leftMs}
          pendingChars={narration.pendingChars ?? null}
          overdue={narration.overdue === true}
          onPlayPause={() => narration.onPlayPause?.()}
          onRate={(r) => narration.onRate?.(r)}
          onNarrate={narration.onNarrate ?? null}
          onRetry={narration.onRetry ?? null}
          notice={cueNotice}
          onSkip={listen ? (sec) => listen.skip(sec) : null}
          sleep={listen ? listen.sleep() : null}
          onSleep={
            listen
              ? (choice) => {
                  listen.setSleep(choice);
                  // The engine holds the timer; this subtree only re-renders on its own tick.
                  tick((n) => n + 1);
                }
              : null
          }
          onListen={listen ? () => setListenOpen(true) : null}
        />
      ) : null}
      {listenOpen && listen ? (
        <ListenView
          controls={listen}
          playing={playing}
          waiting={narration.waiting === true}
          chapterTitle={chapterTitle}
          msLeftInChapter={leftMs}
          rate={narration.clock?.playbackRate ?? 1}
          onPlayPause={() => narration.onPlayPause?.()}
          onRate={(r) => {
            narration.onRate?.(r);
            tick((n) => n + 1);
          }}
          onClose={closeListen}
        />
      ) : null}
      {/*
        ⚠️ The honest wait, and it was written and then never mounted.
        `WaitingBanner` carries six tones including `offline`, and `narration.ts` words the 503 as
        "Karti's voice is offline right now. Pages that are already narrated still play, and this one
        will be generated as soon as it is back." None of that reached a reader: the component had no
        call site, so during a spark-1 outage the play button simply did nothing and said nothing.
        `PlaybackBar` renders only for the ready/playing state; this owns every state where the answer
        is "not yet, and here is why". Rendering both duplicated the same headline and action on a
        cold page, which looked like two separate GPU requests.
      */}
      <WaitingBanner
        state={narration.state ?? "missing"}
        blocked={narration.blocked ?? null}
        pendingChars={narration.pendingChars ?? null}
        etaSec={narration.etaSec ?? null}
        startedAt={narration.waitStartedAt ?? null}
        deadlinePassed={narration.overdue === true}
        onNarrate={narration.onNarrate ?? null}
        onRetry={narration.onRetry ?? null}
        solo={narration.inRoom !== true}
      />
      <div style={{ display: "flex", paddingInline: "var(--space-3)" }}>
        <Scrubber
          positionMs={positionMs}
          durationMs={chapterMs}
          onSeek={onScrub}
          // Inert, visibly, until the chapter has a measured axis. Drawing a rail that cannot be
          // dragged is better than hiding it: the control appearing halfway through a chapter is
          // read as the app changing its mind about what it can do.
          //
          // ⚠️ The axis, and NOT `state === "ready"`. While the engine waits at a page boundary the
          // hook reports the state of the page it is waiting FOR, so the scrubber greyed out for the
          // second or two of every boundary — which is the same "changing its mind" the comment above
          // is about, arriving once a page instead of once a chapter. The chapter axis is still true
          // during a wait, and a scrub during one is a legitimate way out of it.
          disabled={!chapter}
        />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * The typography sheet
 * ══════════════════════════════════════════════════════════════════════════ */

function TypographySheet(props: {
  typo: ReturnType<typeof useTypography>;
  onClose(): void;
}): ReactElement {
  const { typo, onClose } = props;

  // Escape closes it. The drawer does the same; both matter because `usePageTurn` is disabled
  // while either is open, so without a keyboard exit a keyboard user is stuck in the sheet.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <div className="ab-sheet" role="dialog" aria-label="Reading settings" data-no-turn="">
      <div className="ab-field">
        <span className="ab-field-label">Size</span>
        <div className="ab-choices">
          <button
            type="button"
            className="ab-choice"
            onClick={() => typo.stepScale(-1)}
            disabled={typo.prefs.scaleIndex === 0}
            aria-label="Smaller text"
          >
            A−
          </button>
          <button type="button" className="ab-choice" disabled aria-live="polite">
            {Math.round(typo.scale * 100)}%
          </button>
          <button
            type="button"
            className="ab-choice"
            onClick={() => typo.stepScale(1)}
            disabled={typo.prefs.scaleIndex === READER_SCALES.length - 1}
            aria-label="Larger text"
          >
            A+
          </button>
        </div>
      </div>

      <div className="ab-field">
        <span className="ab-field-label">Face</span>
        <div className="ab-choices">
          {READER_FONTS.map((f) => (
            <button
              key={f.key}
              type="button"
              className="ab-choice"
              aria-pressed={typo.prefs.font === f.key}
              title={f.note}
              onClick={() => typo.setPrefs({ font: f.key })}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ab-field">
        <span className="ab-field-label">Theme</span>
        <div className="ab-choices">
          {READER_THEMES.map((t) => (
            <button
              key={t.key}
              type="button"
              className="ab-choice"
              aria-pressed={typo.prefs.theme === t.key}
              onClick={() => typo.setPrefs({ theme: t.key })}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ab-field">
        <span className="ab-field-label">Layout</span>
        <div className="ab-choices">
          {(["auto", "single", "spread"] as const).map((s) => (
            <button
              key={s}
              type="button"
              className="ab-choice"
              aria-pressed={typo.prefs.spread === s}
              onClick={() => typo.setPrefs({ spread: s })}
            >
              {s === "auto" ? "Auto" : s === "single" ? "One page" : "Two pages"}
            </button>
          ))}
          <button
            type="button"
            className="ab-choice"
            aria-pressed={typo.prefs.justify}
            onClick={() => typo.setPrefs({ justify: !typo.prefs.justify })}
          >
            Justify
          </button>
        </div>
      </div>

      <button type="button" className="ab-btn" onClick={onClose}>
        Done
      </button>
    </div>
  );
}

/**
 * `#s=<sid>` — a shared passage (§5.6).
 *
 * `chooseOpening` itself lives in `lib/position.ts`, not here: it is LAW 2's decision point, it
 * shipped with an argument-order bug that silently deleted the whole remap ladder, and nothing under
 * `node --test` can import a module that imports four stylesheets. One implementation, in the file
 * that can be tested.
 */
function sidFromHash(): string | null {
  if (typeof location === "undefined") return null;
  const m = /^#s=(.+)$/.exec(location.hash);
  return m ? validSid(decodeURIComponent(m[1]!)) : null;
}

/**
 * A refusal must name what it refused and what would fix it (AGENTS.md, *Refusals*). "Couldn't
 * process this file" is how someone tries the same thing four times.
 *
 * **404 and not 403**, always, for a book you may not see — LAW 17, so a refusal never confirms
 * that an unlisted book exists. Which means the reader cannot distinguish "gone" from "not yours",
 * and must not pretend to: the copy covers both honestly.
 */
function describeFailure(err: unknown): Phase {
  if (err instanceof HttpError) {
    if (err.status === 404) {
      return {
        k: "error",
        headline: "This book isn't here.",
        detail:
          "It may have been deleted, or the link may be for a book that isn't shared with you. Ask whoever sent it for a fresh link.",
      };
    }
    if (err.status === 401 || err.status === 403) {
      return {
        k: "error",
        headline: "You need to be signed in to read this.",
        detail: "Sign in and open the link again.",
      };
    }
    if (err.status >= 500) {
      return {
        k: "error",
        headline: "The server couldn't send the book.",
        detail: `It answered ${err.status}. This is our fault, not yours — try again in a moment.`,
      };
    }
    return { k: "error", headline: "The book wouldn't open.", detail: `The server answered ${err.status}.` };
  }
  return {
    k: "error",
    headline: "The book wouldn't open.",
    detail: err instanceof Error ? err.message : "An unexpected error while loading the book.",
  };
}

export default Reader;
